import { Edit2, Plus, Search, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import AppButton from "../../components/ui/AppButton";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import { MySelect } from "../../components/ui/Select";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import { cn, formatCurrencyForTable } from "../../lib/utils";
import { PlanConfig, PlanSearchRequest, PlanSearchResponse } from "./@types";
import DeleteCustomPricingModal from "./components/DeleteCustomPricingModal";
import PlanConfigModal from "./components/PlanConfigModal";
import { useGetPlanConfigMutation } from "./subscriptionApiSlice";

const CustomPricingDashboard: React.FC = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [dateAddedFilter, setDateAddedFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedPlanConfig, setSelectedPlanConfig] =
    useState<PlanConfig | null>(null);
  const [openModal, setOpenModal] = useState("");

  // RTK Query mutation
  const [getPlanConfig, { data: maintenanceData, isLoading }] =
    useGetPlanConfigMutation();

  const fetchPlanConfig = useCallback(async () => {
    const requestBody: PlanSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      keyword: searchTerm || undefined,
      dateAdded: dateAddedFilter || undefined,
    };

    try {
      await getPlanConfig(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch expense heads:", err);
    }
  }, [currentPage, pageSize, searchTerm, getPlanConfig]);

  // Fetch expense heads on component mount and when filters change
  useEffect(() => {
    fetchPlanConfig();
  }, [currentPage, pageSize, searchTerm, dateAddedFilter, fetchPlanConfig]);

  // Table columns configuration
  const columns: Column<PlanConfig>[] = [
    {
      key: "companyName",
      header: "Company name",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "monthlyPrice",
      header: "Monthly price",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "maxMonthlyVehicles",
      header: "Max monthly vehicles",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "quarterlyPrice",
      header: "Quarterly price",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "maxQuarterlyVehicles",
      header: "Max quarterly vehicles",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "yearlyPrice",
      header: "Yearly price",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "maxYearlyVehicles",
      header: "Max yearly vehicles",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: PlanConfig) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "Edit",
                onClick: () => {
                  setSelectedPlanConfig(item);
                  setOpenModal("edit");
                },
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "Delete",
                onClick: () => {
                  setSelectedPlanConfig(item);
                  setOpenModal("delete");
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleModal = () => {
    setSelectedPlanConfig(null);
    setOpenModal("");
    fetchPlanConfig();
  };

  const response = maintenanceData?.data as PlanSearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">
              All pricing config
            </h2>
          </div>

          <div className="flex gap-3">
            <AppButton
              onClick={() => setOpenModal("add")}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create
            </AppButton>
          </div>
        </div>

        {/* All expense heads section */}
        <div className="space-y-6 bg-white rounded-[10px] p-4">
          {/* Filters and Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-[16px] pl-9 pr-4 py-2 border border-[#F6F6F6] bg-[#F6F6F6] rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-full sm:w-[367px] h-[42px]"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <MySelect<string>
                  name={"name"}
                  id={"id"}
                  label={""}
                  options={[]}
                  getValue={(option: string) => option}
                  getName={(option: string) => option}
                  value={dateAddedFilter}
                  onChange={(e) => setDateAddedFilter(e)}
                  onValChange={(e) => setDateAddedFilter(e)}
                  placeholder={"Filter"}
                  disabled={isLoading}
                  className={cn(
                    "focus:ring-primary focus:border-primary",
                    {
                      "opacity-50": isLoading,
                    },
                    "w-[70px] h-[42px] text-[13px] px-2 text-textColor appearance-none bg-white border border-[#CECECE] rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  )}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              emptyMessage="No expense heads found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>

      <PlanConfigModal
        isOpen={openModal === "add" || openModal === "edit"}
        onClose={() => handleModal()}
        plan={selectedPlanConfig ? selectedPlanConfig : null}
      />

      {selectedPlanConfig && (
        <DeleteCustomPricingModal
          isOpen={openModal === "delete"}
          onClose={() => handleModal()}
          plan={selectedPlanConfig}
        />
      )}
    </div>
  );
};

export default CustomPricingDashboard;
